/**
 * DryRunPanel — the tenant detail panel's dry-run block (WSRPDM-0012,
 * contract §1.6). "Test sales" / "Test attendance" exercise the real
 * fetch + parse for the backend's default date window; nothing is written.
 *
 * The result is ALWAYS HTTP 200 — outcome is `IngestionTestResult.status`
 * (SUCCESS / NOT_CONFIGURED / FAILED). A bad credential surfaces as
 * `status: 'FAILED'` with a redacted `error`, never a thrown/502 error — only
 * a genuine request failure (network, 400 `INVALID_TEST_RANGE`, 404
 * `UNKNOWN_TENANT`) goes through the catch block below.
 */
import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import { useCopy } from '@/hooks/useCopy';
import { dryRun } from '@/api/wsr/tenants';
import type {
  AttendanceParse,
  GhlFieldCheck,
  IngestionTestResult,
  JobType,
  SampleTable,
  SyncPlan,
} from '@/api/wsr/types';
import type { WStudioVerificationView } from '@/api/wsr/types';
import { DryRunResultTable } from './DryRunResultTable';

/**
 * Handoff §I7 — the GHL field-check results table sorts failures first
 * (MISSING, then UNRESOLVED, then PRESENT) so a broken mapping isn't buried
 * among a dozen healthy rows. `DryRunResultTable` stays a generic renderer
 * (§I4/Non-Goals) — the reorder happens here, on a copy, only for this job
 * type; the other three keep server row order.
 */
const OUTCOME_RANK: Record<string, number> = { MISSING: 0, UNRESOLVED: 1, PRESENT: 2 };

function sortGhlFieldRowsFirst(table: SampleTable): SampleTable {
  const rows = [...table.rows].sort(
    (a, b) => (OUTCOME_RANK[a.outcome as string] ?? 3) - (OUTCOME_RANK[b.outcome as string] ?? 3),
  );
  return { ...table, rows };
}

const PLAN_BAR_HEIGHT = 8;

/**
 * Handoff §F — above this fraction of `rowsSkippedIncomplete / rowsInSheet`,
 * the attendance report's column layout has almost certainly moved (see
 * WSRPDM-0034 spec's "On the 0.1 threshold" note: a layout break fails
 * essentially every row, while ordinary data noise is a handful of rows —
 * 10% sits far above the noise floor and far below a layout break).
 */
const INCOMPLETE_ALARM_RATIO = 0.1;

/**
 * Handoff §G/§H1 — the counts line's copy varies by job type: §H1's matrix makes
 * `recordsInWindow === recordsFetched` for every job type, so a second number never carries
 * information, and §G requires Sales be labelled a sample, not a total. `ATTENDANCE_SYNC` renders
 * `rowsParsed`-equivalent phrasing here (the raw count, not the §F breakdown, which has its own
 * block above). An unmapped `jobType` — this console does not enumerate the type — falls back to
 * the generic phrasing rather than rendering nothing.
 */
function countsCopy(jobType: JobType): { key: string; fallback: string } {
  if (jobType === 'USER_SYNC') {
    return { key: 'tenantDetail.dryRun.counts.user', fallback: '{{n}} users fetched' };
  }
  if (jobType === 'SALES_SYNC') {
    return {
      key: 'tenantDetail.dryRun.counts.sales',
      fallback: '{{n}} records fetched (one page)',
    };
  }
  if (jobType === 'ATTENDANCE_SYNC') {
    return { key: 'tenantDetail.dryRun.counts.attendance', fallback: '{{n}} rows parsed' };
  }
  return { key: 'tenantDetail.dryRun.counts.generic', fallback: '{{n}} records fetched' };
}

function SyncPlanBlock({ plan }: { plan: SyncPlan }) {
  const { t } = useCopy();
  const showAllCreateWarning =
    plan.wstudioUsers > 0 && plan.ghlContacts > 0 && plan.wouldCreate === plan.wstudioUsers;

  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {t('tenantDetail.dryRun.plan.title', 'Sync plan')}
      </Typography>
      <Typography variant="body2">
        {t(
          'tenantDetail.dryRun.plan.population',
          '{{wstudioUsers}} WStudio users · {{ghlContacts}} GoHighLevel contacts',
          { wstudioUsers: plan.wstudioUsers, ghlContacts: plan.ghlContacts },
        )}
      </Typography>
      <Typography variant="body2">
        {t('tenantDetail.dryRun.plan.create', '{{n}} would be created', { n: plan.wouldCreate })}
        {' · '}
        {t('tenantDetail.dryRun.plan.update', '{{n}} would be updated', { n: plan.wouldUpdate })}
        {' · '}
        {t('tenantDetail.dryRun.plan.noChange', '{{n}} already up to date', {
          n: plan.wouldLeaveUnchanged,
        })}
      </Typography>
      {plan.wstudioUsers > 0 ? (
        <Box
          aria-hidden="true"
          sx={{
            display: 'flex',
            height: `${PLAN_BAR_HEIGHT}px`,
            borderRadius: 1,
            overflow: 'hidden',
            mt: 0.5,
          }}
        >
          <Box sx={{ flexGrow: plan.wouldCreate, bgcolor: 'warning.main' }} />
          <Box sx={{ flexGrow: plan.wouldUpdate, bgcolor: 'info.main' }} />
          <Box sx={{ flexGrow: plan.wouldLeaveUnchanged, bgcolor: 'success.main' }} />
        </Box>
      ) : null}
      <Typography variant="body2" sx={{ mt: 0.5 }}>
        {t('tenantDetail.dryRun.plan.unmatched', '{{n}} contacts with no WStudio match', {
          n: plan.ghlContactsUnmatched,
        })}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {t(
          'tenantDetail.dryRun.plan.unmatchedHelp',
          'These are left untouched — the sync never deactivates or removes contacts.',
        )}
      </Typography>
      {plan.lastPurchaseExcluded ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          {t(
            'tenantDetail.dryRun.plan.lastPurchaseFootnote',
            'Last-purchase changes are not checked, so the update count is a minimum.',
          )}
        </Typography>
      ) : null}
      {plan.userPullCapped ? (
        <Alert severity="warning" sx={{ mt: 1 }}>
          {t(
            'tenantDetail.dryRun.plan.cappedWarning',
            'The WStudio user pull was capped — these counts cover only part of the population.',
          )}
        </Alert>
      ) : null}
      {showAllCreateWarning ? (
        <Alert severity="warning" sx={{ mt: 1 }}>
          {t(
            'tenantDetail.dryRun.plan.allCreateWarning',
            'GoHighLevel holds {{ghlContacts}} contacts, yet all {{wstudioUsers}} WStudio users would be created rather than matched. The contact.wstudio_id mapping is pointing at the wrong GoHighLevel field, and a real sync would duplicate every contact. Verify the mapping before enabling sync.',
            { ghlContacts: plan.ghlContacts, wstudioUsers: plan.wstudioUsers },
          )}
        </Alert>
      ) : null}
    </Box>
  );
}

function AttendanceParseBlock({ parse }: { parse: AttendanceParse }) {
  const { t } = useCopy();
  const showIncompleteWarning =
    parse.rowsInSheet > 0 &&
    parse.rowsSkippedIncomplete / parse.rowsInSheet >= INCOMPLETE_ALARM_RATIO;

  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {t('tenantDetail.dryRun.attendance.title', 'Report parse')}
      </Typography>
      <Typography variant="body2">
        {t('tenantDetail.dryRun.attendance.rowsInSheet', '{{n}} rows in the report', {
          n: parse.rowsInSheet,
        })}
      </Typography>
      <Typography variant="body2">
        {t('tenantDetail.dryRun.attendance.parsed', '{{n}} parsed', { n: parse.rowsParsed })}
        {' · '}
        {t('tenantDetail.dryRun.attendance.openGym', '{{n}} open-gym rows skipped', {
          n: parse.rowsSkippedOpenGym,
        })}
        {' · '}
        {t('tenantDetail.dryRun.attendance.incomplete', '{{n}} incomplete rows skipped', {
          n: parse.rowsSkippedIncomplete,
        })}
      </Typography>
      {parse.rowsInSheet > 0 ? (
        <Box
          aria-hidden="true"
          sx={{
            display: 'flex',
            height: `${PLAN_BAR_HEIGHT}px`,
            borderRadius: 1,
            overflow: 'hidden',
            mt: 0.5,
          }}
        >
          <Box sx={{ flexGrow: parse.rowsParsed, bgcolor: 'success.main' }} />
          <Box sx={{ flexGrow: parse.rowsSkippedOpenGym, bgcolor: 'grey.400' }} />
          <Box sx={{ flexGrow: parse.rowsSkippedIncomplete, bgcolor: 'warning.main' }} />
        </Box>
      ) : null}
      {parse.rowsInSheet === 0 ? (
        <Alert severity="info" sx={{ mt: 1 }}>
          {t(
            'tenantDetail.dryRun.attendance.emptyReport',
            'The report generated successfully but contained no rows for this window.',
          )}
        </Alert>
      ) : null}
      {showIncompleteWarning ? (
        <Alert severity="warning" sx={{ mt: 1 }}>
          {t(
            'tenantDetail.dryRun.attendance.incompleteWarning',
            "{{n}} of {{total}} rows arrived without a date, time or member. That usually means the attendance report's column layout has moved — check the report before trusting this sync.",
            { n: parse.rowsSkippedIncomplete, total: parse.rowsInSheet },
          )}
        </Alert>
      ) : null}
    </Box>
  );
}

/**
 * Handoff §I7 — the match-key banner is visually distinct from the counts,
 * not one red row among fourteen: `contact.wstudio_id` failing is the
 * duplicate-contact scenario in §I1, so it gets its own alert above the
 * counts rather than a table row. §I6 — MISSING and UNRESOLVED are kept as
 * two separate counts, never merged into one "bad" total; they have
 * different causes (a deleted/recreated GHL field vs. a fieldKey that never
 * matched anything) and different fixes.
 */
function GhlFieldCheckBlock({ check }: { check: GhlFieldCheck }) {
  const { t } = useCopy();
  const needsRepair = check.missing > 0 || check.unresolved > 0;

  return (
    <Box sx={{ mt: 1 }}>
      {!check.wstudioIdOk ? (
        <Alert severity="error" sx={{ mb: 1 }}>
          {t(
            'tenantDetail.dryRun.ghlFields.matchKeyBroken',
            'contact.wstudio_id — the field used to match WStudio users to GoHighLevel contacts — is {{outcome}}. Until it is fixed, a real sync would match nothing and create a duplicate contact for every user on every run.',
            { outcome: check.wstudioIdOutcome },
          )}
        </Alert>
      ) : null}
      <Typography variant="body2">
        {t('tenantDetail.dryRun.ghlFields.checked', '{{n}} mappings checked', {
          n: check.fieldsChecked,
        })}
        {' · '}
        {t('tenantDetail.dryRun.ghlFields.present', '{{n}} present', { n: check.present })}
        {' · '}
        {t('tenantDetail.dryRun.ghlFields.missing', '{{n}} missing from GoHighLevel', {
          n: check.missing,
        })}
        {' · '}
        {t('tenantDetail.dryRun.ghlFields.unresolved', '{{n}} never resolved', {
          n: check.unresolved,
        })}
      </Typography>
      {needsRepair ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          {t(
            'tenantDetail.dryRun.ghlFields.repairHelp',
            'Missing fields must be created or renamed in GoHighLevel, then re-run this test. The console cannot create them.',
          )}
        </Typography>
      ) : null}
    </Box>
  );
}

function RequestUrlLine({ url }: { url: string | null | undefined }) {
  const { t } = useCopy();
  if (!url) return null;

  return (
    <Box sx={{ mt: 0.5 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {t('tenantDetail.dryRun.requestUrl.label', 'WStudio request')}
      </Typography>
      <Box
        component="code"
        sx={{ display: 'block', fontSize: '0.75rem', overflowWrap: 'anywhere' }}
      >
        {url}
      </Box>
    </Box>
  );
}

interface DryRunPanelProps {
  slug: string;
  verification?: WStudioVerificationView | undefined;
  onRunComplete?: (() => void | Promise<void>) | undefined;
}

interface RunState {
  running: boolean;
  result: IngestionTestResult | null;
  requestError: string | null;
}

const INITIAL_RUN_STATE: RunState = { running: false, result: null, requestError: null };

function ResultStatusAlert({
  result,
  showSampleFallback,
}: {
  result: IngestionTestResult;
  showSampleFallback: boolean;
}) {
  const { t } = useCopy();

  if (result.status === 'NOT_CONFIGURED') {
    return (
      <Alert severity="warning" sx={{ mt: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {t('tenantDetail.dryRun.notConfigured', 'Not configured')}
        </Typography>
        {result.error ? <Typography variant="body2">{result.error}</Typography> : null}
        <RequestUrlLine url={result.wstudioRequestUrl} />
      </Alert>
    );
  }

  if (result.status === 'FAILED') {
    return (
      <Alert severity="error" sx={{ mt: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {t('tenantDetail.dryRun.failure', 'Failed')}
        </Typography>
        <Box component="pre" sx={{ m: 0, mt: 0.5, whiteSpace: 'pre-wrap', fontSize: '0.75rem' }}>
          {result.error ?? t('tenantDetail.dryRun.unknownError', 'Unknown error')}
        </Box>
        <RequestUrlLine url={result.wstudioRequestUrl} />
      </Alert>
    );
  }

  const counts = countsCopy(result.jobType);

  return (
    <Alert severity="success" sx={{ mt: 1 }}>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {t('tenantDetail.dryRun.success', 'Success')}
      </Typography>
      <Typography variant="body2">
        {t(counts.key, counts.fallback, { n: result.recordsFetched })}
      </Typography>
      {result.jobType === 'SALES_SYNC' ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {t(
            'tenantDetail.dryRun.counts.salesHelp',
            "One page at the configured maximum — not the tenant's sales total, and not necessarily the most recent sales.",
          )}
        </Typography>
      ) : null}
      {result.syncPlan ? <SyncPlanBlock plan={result.syncPlan} /> : null}
      {result.attendanceParse ? <AttendanceParseBlock parse={result.attendanceParse} /> : null}
      {result.ghlFieldCheck ? <GhlFieldCheckBlock check={result.ghlFieldCheck} /> : null}
      <RequestUrlLine url={result.wstudioRequestUrl} />
      {result.warnings.length > 0 ? (
        <Box sx={{ mt: 0.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            {t('tenantDetail.dryRun.warnings', 'Warnings')}
          </Typography>
          <List dense disablePadding>
            {result.warnings.map((warning, idx) => (
              <ListItem key={`${idx}-${warning}`} disablePadding sx={{ py: 0 }}>
                <ListItemText slotProps={{ primary: { variant: 'caption' } }} primary={warning} />
              </ListItem>
            ))}
          </List>
        </Box>
      ) : null}
      {showSampleFallback && result.sample.length > 0 ? (
        <Box sx={{ mt: 0.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            {t('tenantDetail.dryRun.sampleData', 'Sample data')}
            {' — '}
            {t('tenantDetail.dryRun.sampleRows', '{{n}} rows', { n: result.sample.length })}
          </Typography>
          <Box component="pre" sx={{ m: 0, mt: 0.5, whiteSpace: 'pre-wrap', fontSize: '0.75rem' }}>
            {result.sample.join('\n')}
          </Box>
        </Box>
      ) : null}
    </Alert>
  );
}

function ResultAlert({ result }: { result: IngestionTestResult }) {
  const hasTable = Boolean(result.sampleTable && result.sampleTable.columns.length > 0);
  const table =
    hasTable && result.sampleTable
      ? result.ghlFieldCheck
        ? sortGhlFieldRowsFirst(result.sampleTable)
        : result.sampleTable
      : null;

  return (
    <>
      <ResultStatusAlert result={result} showSampleFallback={!hasTable} />
      {table ? <DryRunResultTable table={table} recordsFetched={result.recordsFetched} /> : null}
    </>
  );
}

const JOB_TYPES = {
  sales: 'SALES_SYNC',
  attendance: 'ATTENDANCE_SYNC',
  user: 'USER_SYNC',
  'ghl-fields': 'GHL_FIELD_CHECK',
} as const;

function DryRunKind({
  slug,
  kind,
  verification,
  onRunComplete,
}: {
  slug: string;
  kind: 'sales' | 'attendance' | 'user' | 'ghl-fields';
  verification?: WStudioVerificationView | undefined;
  onRunComplete?: (() => void | Promise<void>) | undefined;
}) {
  const { t } = useCopy();
  const [state, setState] = useState<RunState>(INITIAL_RUN_STATE);

  const title = {
    sales: t('tenantDetail.dryRun.salesTitle', 'Sales dry-run'),
    attendance: t('tenantDetail.dryRun.attendanceTitle', 'Attendance dry-run'),
    user: t('tenantDetail.dryRun.userTitle', 'User dry-run'),
    'ghl-fields': t('tenantDetail.dryRun.ghlFieldsTitle', 'GHL field check'),
  }[kind];
  const verified = verification?.jobs.find((job) => job.jobType === JOB_TYPES[kind]);
  const status = verified?.stale
    ? t('tenantDetail.verification.stale', 'Stale — re-run needed')
    : verified?.status === 'SUCCESS'
      ? t('tenantDetail.verification.verified', 'Verified')
      : verified?.status === 'FAILED' || verified?.status === 'NOT_CONFIGURED'
        ? t('tenantDetail.verification.failed', 'Failed')
        : t('tenantDetail.verification.notRun', 'Not run');

  const handleRun = async () => {
    setState({ running: true, result: null, requestError: null });
    try {
      const result = await dryRun(slug, kind);
      setState({ running: false, result, requestError: null });
      await onRunComplete?.();
    } catch {
      setState({
        running: false,
        result: null,
        requestError: t(
          'tenantDetail.dryRun.runError',
          'Dry-run request failed. Please try again.',
        ),
      });
    }
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {status}
        </Typography>
        <Button
          type="button"
          size="small"
          onClick={() => void handleRun()}
          disabled={state.running}
          aria-label={title}
        >
          {state.running
            ? t('tenantDetail.dryRun.running', 'Running…')
            : t('tenantDetail.dryRun.run', 'Run test')}
        </Button>
        {state.running ? <CircularProgress size={16} /> : null}
      </Box>
      {state.requestError ? (
        <Alert severity="error" sx={{ mt: 1 }}>
          {state.requestError}
        </Alert>
      ) : null}
      {state.result ? <ResultAlert result={state.result} /> : null}
    </Box>
  );
}

export function DryRunPanel({ slug, verification, onRunComplete }: DryRunPanelProps) {
  const { t } = useCopy();
  return (
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
        {t('tenantDetail.dryRun.title', 'Dry-run tests')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t(
          'tenantDetail.dryRun.subtitle',
          'Validate before enabling — exercise the real fetch + parse for a date range but write nothing.',
        )}
      </Typography>
      <DryRunKind
        slug={slug}
        kind="sales"
        verification={verification}
        onRunComplete={onRunComplete}
      />
      <DryRunKind
        slug={slug}
        kind="attendance"
        verification={verification}
        onRunComplete={onRunComplete}
      />
      <DryRunKind
        slug={slug}
        kind="user"
        verification={verification}
        onRunComplete={onRunComplete}
      />
      <DryRunKind
        slug={slug}
        kind="ghl-fields"
        verification={verification}
        onRunComplete={onRunComplete}
      />
      <Typography variant="caption" color="text.secondary">
        {t(
          'tenantDetail.dryRun.writeNothing',
          'Dry-run only — no data is written, no watermarks are moved.',
        )}
      </Typography>
    </Box>
  );
}
